import { toAIRelayRequest, type AIProvider, type AIRelayProvider } from '@aibi/ai-core';
import { AppError, err, ok, type ErrorCode } from '@aibi/shared';
import type { AIRelayApi } from './ai-relay-api';

/** Errores que indican que el proveedor del servidor no esta funcionando. */
const PROVIDER_FAULTS: ReadonlySet<ErrorCode> = new Set([
  'TIMEOUT',
  'UNAVAILABLE',
  'UPSTREAM_ERROR',
  'PAYLOAD_TOO_LARGE',
]);

const DISPLAY_NAMES: Readonly<Record<ServerProviderKind, string>> = {
  n8n: 'n8n (servidor)',
  'azure-ai': 'Azure AI Foundry (servidor)',
};

type ServerProviderKind = Exclude<AIRelayProvider, 'unconfigured'>;

export interface ServerAIProviderOptions {
  readonly api: AIRelayApi;
  /** Proveedor que el servidor declara en `/api/ai/status`; por defecto `n8n`. */
  readonly kind?: ServerProviderKind;
  readonly displayName?: string;
  /** Resultado de cada llamada real (no se notifica la cancelacion). */
  readonly onOutcome?: (outcome: 'ok' | 'failed') => void;
}

/**
 * Proveedor de IA que delega en el relay del servidor (`POST /api/ai/generate`). Solo
 * envia los campos del protocolo del relay (mensaje, contexto acotado y acciones
 * permitidas); el servidor reconstruye instrucciones y JSON Schema. El contenido devuelto
 * vuelve a pasar por el pipeline completo del AI Core en el navegador.
 *
 * @example
 * const core = createAICore({ provider: createServerAIProvider({ api }) });
 */
export function createServerAIProvider(options: ServerAIProviderOptions): AIProvider {
  const kind = options.kind ?? 'n8n';
  return {
    id: `server-${kind}`,
    kind,
    displayName: options.displayName ?? DISPLAY_NAMES[kind],
    capabilities: { structuredOutput: false, streaming: false },
    async generate(request, signal) {
      const result = await options.api.generate(toAIRelayRequest(request), signal);
      if (!result.ok) {
        if (PROVIDER_FAULTS.has(result.error.code)) options.onOutcome?.('failed');
        return result;
      }
      if (result.value.requestId !== request.requestId) {
        options.onOutcome?.('failed');
        return err(
          new AppError(
            'UPSTREAM_ERROR',
            'La respuesta del servidor no corresponde a la solicitud.',
          ),
        );
      }
      options.onOutcome?.('ok');
      return ok({ providerId: result.value.providerId, content: result.value.content });
    },
  };
}
